import { forwardRef, useState, type FocusEvent } from 'react';

import { TextField, type TextFieldProps } from './TextField';

export type PasswordInputProps = Omit<TextFieldProps, 'type' | 'controlEnd'> & {
  /** Accessible label for the show toggle (UB6). */
  showLabel: string;
  /** Accessible label for the hide toggle (UB6). */
  hideLabel: string;
};

export const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
  function PasswordInput({ showLabel, hideLabel, onBlur, ...rest }, ref) {
    const [visible, setVisible] = useState(false);

    // UB5.1 🔹 Déduit — reset to masked on blur so the password is never left visible
    // when the user tabs away from the field (Spec V1.1 §4.2 ll.389-399).
    const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
      setVisible(false);
      onBlur?.(event);
    };

    const toggle = (
      <button
        type="button"
        className="m5-eye-toggle"
        aria-label={visible ? hideLabel : showLabel}
        aria-pressed={visible}
        onClick={() => setVisible((v) => !v)}
      >
        {visible ? <EyeOffIcon /> : <EyeIcon />}
      </button>
    );

    return (
      <TextField
        {...rest}
        ref={ref}
        type={visible ? 'text' : 'password'}
        onBlur={handleBlur}
        controlEnd={toggle}
      />
    );
  },
);

function EyeIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function EyeOffIcon() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="m3 3 18 18" />
      <path d="M10.6 6.1A10.9 10.9 0 0 1 12 6c6.5 0 10 6 10 6a17.5 17.5 0 0 1-3.2 3.9" />
      <path d="M6.6 6.6A17.5 17.5 0 0 0 2 12s3.5 6 10 6c1.6 0 3-.4 4.3-1" />
      <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    </svg>
  );
}
